import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
// Heavy (jsPDF): loaded only when the user exports the plan.
const loadBreaksExport = () => import("@/lib/breaks-export");
import { AssistantMessage, RichText } from "@/components/AssistantMessage";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowLeft, Loader2, Sparkles, Check, SkipForward, Play, Plus, Trash2,
  BookOpen, AlertTriangle, ClipboardCheck, Wand2, ListChecks, Timer as TimerIcon, Download,
} from "lucide-react";
import { toast } from "sonner";
import { MediaAttachButton } from "@/components/MediaAttachButton";
import {
  parseAssignments, savePeriodItems, buildPeriodPlan, setTaskStatus, deletePeriodItem,
  generateDiagnostic, generateExercises, correctExercise, simulateExam, askPlanAdjustment,
  updateReadiness, updatePeriodItem, deleteStudyPeriod,
  type ParsedItemT,
} from "@/lib/holidays.functions";

export const Route = createFileRoute("/_app/breaks/$periodId")({
  head: () => ({
    meta: [
      { title: "Il tuo piano · Lumen" },
      { name: "description", content: "Il tuo piano giornaliero per compiti delle vacanze, recupero o preparazione all'esame." },
      { property: "og:title", content: "Il tuo piano · Lumen" },
      { property: "og:description", content: "Poco, regolare e intelligente." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: PeriodPage,
});

const todayISO = () => new Date().toISOString().slice(0, 10);

type PeriodRow = {
  id: string; kind: "summer" | "recovery" | "exam"; title: string; subject_name: string | null;
  start_date: string | null; end_date: string | null; exam_date: string | null;
  daily_minutes: number; readiness: number; age_band: string; scope: string | null;
  plan: { summary?: string; phases?: { label: string; focus: string }[] } | null;
};
type ItemRow = {
  id: string; kind: string; subject: string; title: string; detail: string | null;
  unit_label: string; total_units: number; done_units: number; difficulty: string;
  minutes_per_unit: number; due_date: string | null; final_task: string | null;
};
type TaskRow = {
  id: string; task_date: string; subject: string; title: string; detail: string | null;
  minutes: number; units: number; priority: string; phase: string | null; status: string;
};

function PeriodPage() {
  const { periodId } = Route.useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const period = useQuery({
    queryKey: ["study-period", periodId],
    queryFn: async () => {
      const { data } = await supabase.from("study_periods").select("*").eq("id", periodId).maybeSingle();
      return data as PeriodRow | null;
    },
  });
  const items = useQuery({
    queryKey: ["study-period-items", periodId],
    queryFn: async () => {
      const { data } = await supabase.from("study_period_items").select("*").eq("period_id", periodId).order("position");
      return (data ?? []) as ItemRow[];
    },
  });
  const tasks = useQuery({
    queryKey: ["study-period-tasks", periodId],
    queryFn: async () => {
      const { data } = await supabase
        .from("study_period_tasks").select("*").eq("period_id", periodId)
        .gte("task_date", todayISO()).order("task_date").limit(120);
      return (data ?? []) as TaskRow[];
    },
  });

  const p = period.data;
  const deadline = p?.exam_date ?? p?.end_date ?? null;
  const daysLeft = deadline
    ? Math.max(0, Math.round((new Date(`${deadline}T00:00:00Z`).getTime() - Date.now()) / 86400000))
    : null;

  const today = (tasks.data ?? []).filter((x) => x.task_date === todayISO());
  const upcoming = (tasks.data ?? []).filter((x) => x.task_date > todayISO());

  async function remove() {
    await deleteStudyPeriod({ data: { id: periodId } });
    qc.invalidateQueries({ queryKey: ["study-periods"] });
    navigate({ to: "/breaks" });
  }

  if (period.isLoading) {
    return <div className="px-5 py-8"><LoadingState /></div>;
  }
  if (period.isError) {
    return <div className="px-5 py-8"><ErrorState error={period.error} onRetry={() => period.refetch()} /></div>;
  }
  if (!p) {
    return (
      <div className="px-5 py-8">
        <EmptyState title={t("states.notFoundTitle")} description={t("states.notFoundBody")} />
      </div>
    );
  }

  return (
    <div className="space-y-5 px-5 pt-6 pb-6">
      <div className="flex items-center gap-2">
        <Button size="icon" variant="ghost" onClick={() => navigate({ to: "/breaks" })}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-lg font-semibold">{p.title}</h1>
          <p className="text-xs text-muted-foreground">
            {deadline ? (p.exam_date ? t("breaks.plan.testOn", { date: deadline }) : deadline) : ""}
            {daysLeft !== null ? ` · ${t("breaks.plan.daysLeft", { n: daysLeft })}` : ""}
          </p>
        </div>
        <Button
          size="icon"
          variant="ghost"
          aria-label={t("breaks.plan.export", { defaultValue: "Scarica il piano in PDF" })}
          onClick={async () => {
            const { exportPlanPdf } = await loadBreaksExport();
            exportPlanPdf(
              p as never,
              (items.data ?? []) as never,
              (tasks.data ?? []) as never,
            );
          }}
        >
          <Download className="h-4 w-4" />
        </Button>
        <Button size="icon" variant="ghost" onClick={remove}><Trash2 className="h-4 w-4" /></Button>
      </div>

      <ReadinessCard period={p} tasks={tasks.data ?? []} />

      <Tabs defaultValue="plan">
        <TabsList className="grid w-full grid-cols-3 rounded-2xl">
          <TabsTrigger value="plan" className="rounded-xl text-xs">{t("breaks.plan.today")}</TabsTrigger>
          <TabsTrigger value="work" className="rounded-xl text-xs">{t("breaks.items.title")}</TabsTrigger>
          <TabsTrigger value="study" className="rounded-xl text-xs">{t("breaks.study.exercises")}</TabsTrigger>
        </TabsList>

        <TabsContent value="plan" className="mt-4 space-y-4">
          <PlanActions period={p} hasItems={(items.data ?? []).length > 0} />
          {p.plan?.summary && (
            <Card className="card-soft space-y-2 rounded-3xl border-0 p-4">
              <div className="text-sm font-semibold">{t("breaks.plan.summary")}</div>
              <AssistantMessage content={p.plan.summary} className="text-sm leading-relaxed text-muted-foreground" />
              {!!p.plan.phases?.length && (
                <ul className="space-y-1 pt-1">
                  {p.plan.phases.map((ph, i) => (
                    <li key={i} className="text-xs">
                      <span className="font-medium">{ph.label}</span>
                      <span className="text-muted-foreground"> — {ph.focus}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
          <TodayCard tasks={today} periodId={periodId} />
          {!!upcoming.length && (
            <Card className="card-soft space-y-2 rounded-3xl border-0 p-4">
              <div className="text-sm font-semibold">{t("breaks.plan.upcoming")}</div>
              {upcoming.slice(0, 20).map((x) => (
                <div key={x.id} className="flex items-center gap-2 text-xs">
                  <span className="w-16 shrink-0 text-muted-foreground">{x.task_date.slice(5)}</span>
                  <span className="min-w-0 flex-1 truncate">{x.title}</span>
                  <span className="shrink-0 text-muted-foreground">{x.minutes}′</span>
                </div>
              ))}
            </Card>
          )}
          <AskCard periodId={periodId} />
        </TabsContent>

        <TabsContent value="work" className="mt-4 space-y-4">
          <ItemsSection periodId={periodId} ageBand={p.age_band} items={items.data ?? []} />
        </TabsContent>

        <TabsContent value="study" className="mt-4 space-y-4">
          <StudySection period={p} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* --------------------------- READINESS ----------------------------------- */

function ReadinessCard({ period, tasks }: { period: PeriodRow; tasks: TaskRow[] }) {
  const { t } = useTranslation();
  const done = tasks.filter((x) => x.status === "done").length;
  const progress = tasks.length ? Math.round((done / tasks.length) * 100) : 0;
  const r = period.readiness;
  const status = r >= 80 ? "ready" : r >= 60 ? "almost" : r >= 35 ? "recovering" : "weak";
  return (
    <Card className="card-soft space-y-3 rounded-3xl border-0 p-5">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs text-muted-foreground">{t("breaks.plan.readiness")}</div>
          <div className="text-3xl font-semibold">{r}%</div>
        </div>
        <Badge variant="secondary" className="rounded-full text-xs">{t(`breaks.plan.status.${status}`)}</Badge>
      </div>
      <Progress value={r} className="h-2" />
      <div className="text-xs text-muted-foreground">
        {t("breaks.plan.progress")}: {progress}% · {done}/{tasks.length}
      </div>
    </Card>
  );
}

/* --------------------------- PLAN ACTIONS -------------------------------- */

function PlanActions({ period, hasItems }: { period: PeriodRow; hasItems: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const build = useServerFn(buildPeriodPlan);
  const [busy, setBusy] = useState<null | string>(null);

  async function run(mode: "fresh" | "reschedule" | "behind") {
    setBusy(mode);
    try {
      const res = await build({ data: { periodId: period.id, from: null, mode, note: "" } });
      qc.invalidateQueries({ queryKey: ["study-period", period.id] });
      qc.invalidateQueries({ queryKey: ["study-period-tasks", period.id] });
      toast.success(res.summary.slice(0, 120));
    } catch (e) {
      const msg = e instanceof Error ? e.message : "";
      toast.error(msg === "NO_DAYS" ? t("breaks.plan.noDays") : msg || t("breaks.errors.generic"));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-2">
      <Button className="w-full" disabled={!hasItems && period.kind === "summer"} onClick={() => run("fresh")}>
        {busy === "fresh" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
        {busy === "fresh" ? t("breaks.plan.building") : t("breaks.plan.build")}
      </Button>
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" size="sm" onClick={() => run("reschedule")} disabled={!!busy}>
          {busy === "reschedule" ? <Loader2 className="h-4 w-4 animate-spin" /> : t("breaks.plan.reschedule")}
        </Button>
        <Button variant="outline" size="sm" onClick={() => run("behind")} disabled={!!busy}>
          {busy === "behind" ? <Loader2 className="h-4 w-4 animate-spin" /> : t("breaks.plan.behind")}
        </Button>
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">{t("breaks.plan.rescheduleHint")}</p>
    </div>
  );
}

/* ------------------------------ TODAY ------------------------------------ */

function TodayCard({ tasks, periodId }: { tasks: TaskRow[]; periodId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const setStatus = useServerFn(setTaskStatus);
  const bumpReadiness = useServerFn(updateReadiness);
  const [budget, setBudget] = useState<number | null>(null);

  const todo = tasks.filter((x) => x.status === "todo");
  const shown = useMemo(() => {
    if (!budget) return tasks;
    const rank: Record<string, number> = { high: 0, medium: 1, low: 2 };
    const sorted = [...todo].sort((a, b) => (rank[a.priority] ?? 1) - (rank[b.priority] ?? 1));
    const out: TaskRow[] = [];
    let used = 0;
    for (const x of sorted) {
      if (used + x.minutes > budget) continue;
      out.push(x); used += x.minutes;
    }
    return out;
  }, [tasks, todo, budget]);

  const total = todo.reduce((s, x) => s + x.minutes, 0);

  async function mark(id: string, status: "done" | "skipped") {
    await setStatus({ data: { id, status } });
    await bumpReadiness({ data: { periodId, signal: null } });
    qc.invalidateQueries({ queryKey: ["study-period-tasks", periodId] });
    qc.invalidateQueries({ queryKey: ["study-period", periodId] });
    qc.invalidateQueries({ queryKey: ["study-period-items", periodId] });
  }

  function start(x: TaskRow) {
    localStorage.setItem("lumen.session-intent", JSON.stringify({
      topic: x.subject || x.title, goal: x.detail || x.title, minutes: x.minutes, method: "countdown",
    }));
    navigate({ to: "/studio" });
  }

  return (
    <Card className="card-soft space-y-3 rounded-3xl border-0 p-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">{t("breaks.plan.today")}</div>
        <span className="text-xs text-muted-foreground">{t("breaks.plan.totalToday", { min: total })}</span>
      </div>

      {!tasks.length && <p className="text-xs text-muted-foreground">{t("breaks.plan.todayEmpty")}</p>}

      {tasks.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          {[15, 30, 45].map((m) => (
            <button
              key={m}
              onClick={() => setBudget(budget === m ? null : m)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                budget === m ? "bg-primary text-primary-foreground" : "bg-muted text-foreground/70"
              }`}
            >
              {t("breaks.plan.onlyMinutes", { n: m })}
            </button>
          ))}
        </div>
      )}

      {shown.map((x) => (
        <div key={x.id} className={`rounded-2xl p-3 ${x.status === "done" ? "bg-primary/10" : "bg-muted/50"}`}>
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {x.subject && <Badge variant="secondary" className="rounded-full text-xs">{x.subject}</Badge>}
                <span className="text-sm font-medium">{x.title}</span>
              </div>
              {x.detail && <p className="mt-0.5 text-xs text-muted-foreground">{x.detail}</p>}
              <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                <TimerIcon className="h-3 w-3" /> {x.minutes} min
                {x.phase && <span>· {x.phase}</span>}
              </div>
            </div>
          </div>
          {x.status === "todo" && (
            <div className="mt-2 flex gap-1.5">
              <Button size="sm" className="h-7 flex-1 text-xs" onClick={() => start(x)}>
                <Play className="mr-1 h-3 w-3" /> {t("breaks.plan.start")}
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => mark(x.id, "done")}>
                <Check className="h-3 w-3" />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => mark(x.id, "skipped")}>
                <SkipForward className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      ))}
    </Card>
  );
}

/* ------------------------------- ASK AI ---------------------------------- */

function AskCard({ periodId }: { periodId: string }) {
  const { t } = useTranslation();
  const ask = useServerFn(askPlanAdjustment);
  const [msg, setMsg] = useState("");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);

  async function send() {
    if (!msg.trim()) return;
    setBusy(true); setOut("");
    try {
      const r = await ask({ data: { periodId, message: msg.trim() } });
      setOut(r.text);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("breaks.errors.generic"));
    } finally { setBusy(false); }
  }

  return (
    <Card className="card-soft space-y-2 rounded-3xl border-0 p-4">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Wand2 className="h-4 w-4 text-primary" /> {t("breaks.plan.ask")}
      </div>
      <Textarea rows={2} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder={t("breaks.plan.askPh")} />
      <Button size="sm" onClick={send} disabled={busy}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("breaks.plan.send")}
      </Button>
      {out && <AssistantMessage content={out} className="rounded-2xl bg-muted/50 p-3 text-sm leading-relaxed" />}
    </Card>
  );
}

/* ------------------------------- ITEMS ----------------------------------- */

function ItemsSection({ periodId, ageBand, items }: { periodId: string; ageBand: string; items: ItemRow[] }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const parse = useServerFn(parseAssignments);
  const save = useServerFn(savePeriodItems);
  const del = useServerFn(deletePeriodItem);
  const patch = useServerFn(updatePeriodItem);

  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<ParsedItemT[] | null>(null);
  const [bookOpen, setBookOpen] = useState(false);

  async function runParse(source: string, value: string) {
    if (value.trim().length < 3) return;
    setBusy(true);
    try {
      const r = await parse({ data: { text: value, ageBand: ageBand as "primary" | "middle" | "high" | "university", source } });
      setDraft(r.items);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("breaks.errors.generic"));
    } finally { setBusy(false); }
  }

  async function confirmSave() {
    if (!draft?.length) return;
    setBusy(true);
    try {
      await save({ data: { periodId, items: draft, source: "ai" } });
      setDraft(null); setText("");
      qc.invalidateQueries({ queryKey: ["study-period-items", periodId] });
      toast.success("✓");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("breaks.errors.generic"));
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-4">
      <Card className="card-soft space-y-3 rounded-3xl border-0 p-4">
        <div className="text-sm font-semibold">{t("breaks.items.add")}</div>
        <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("breaks.items.textPh")} />
        <MediaAttachButton onText={(txt) => { setText((p) => `${p}\n${txt}`.trim()); void runParse("photo", txt); }} />
        <p className="text-xs text-muted-foreground">{t("breaks.items.photoHint")}</p>
        <div className="flex gap-2">
          <Button size="sm" className="flex-1" disabled={busy || text.trim().length < 3} onClick={() => runParse("text", text)}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("breaks.items.parse")}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setBookOpen(true)}>
            <BookOpen className="mr-1 h-3.5 w-3.5" /> {t("breaks.items.addBook")}
          </Button>
        </div>
      </Card>

      <Card className="card-soft space-y-2 rounded-3xl border-0 p-4">
        <div className="text-sm font-semibold">{t("breaks.items.title")}</div>
        {!items.length && <p className="text-xs text-muted-foreground">{t("breaks.items.empty")}</p>}
        {items.map((it) => {
          const left = Math.max(0, it.total_units - it.done_units);
          const pct = it.total_units ? Math.round((it.done_units / it.total_units) * 100) : 0;
          return (
            <div key={it.id} className="space-y-1.5 rounded-2xl bg-muted/50 p-3">
              <div className="flex items-center gap-2">
                {it.subject && <Badge variant="secondary" className="rounded-full text-xs">{it.subject}</Badge>}
                {it.kind === "book" && <BookOpen className="h-3.5 w-3.5 text-primary" />}
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{it.title}</span>
                <Button
                  size="icon" variant="ghost" className="h-6 w-6"
                  onClick={async () => { await del({ data: { id: it.id } }); qc.invalidateQueries({ queryKey: ["study-period-items", periodId] }); }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
              {!!it.total_units && (
                <>
                  <Progress value={pct} className="h-1.5" />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{t("breaks.items.left", { left, total: it.total_units, unit: it.unit_label })}</span>
                    <input
                      type="number"
                      className="w-16 rounded-lg bg-background px-2 py-0.5 text-right text-xs"
                      defaultValue={it.done_units}
                      onBlur={async (e) => {
                        const v = Math.max(0, Math.min(it.total_units, Number(e.target.value) || 0));
                        await patch({ data: { id: it.id, patch: { done_units: v } } });
                        qc.invalidateQueries({ queryKey: ["study-period-items", periodId] });
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          );
        })}
      </Card>

      {draft && (
        <Dialog open onOpenChange={(o) => !o && setDraft(null)}>
          <DialogContent className="max-h-[85vh] overflow-y-auto rounded-3xl sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-base">{t("breaks.items.confirmTitle")}</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground">{t("breaks.items.confirmHint")}</p>
            <div className="space-y-2">
              {draft.map((d, i) => (
                <div key={i} className="space-y-1.5 rounded-2xl bg-muted/50 p-3">
                  <div className="flex gap-2">
                    <Input
                      className="h-8 text-xs" value={d.subject}
                      onChange={(e) => setDraft((p) => p!.map((x, j) => (j === i ? { ...x, subject: e.target.value } : x)))}
                    />
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDraft((p) => p!.filter((_, j) => j !== i))}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <Input
                    className="h-8 text-xs" value={d.title}
                    onChange={(e) => setDraft((p) => p!.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
                  />
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Input
                      type="number" className="h-7 w-20 text-xs" value={d.totalUnits}
                      onChange={(e) => setDraft((p) => p!.map((x, j) => (j === i ? { ...x, totalUnits: Number(e.target.value) || 0 } : x)))}
                    />
                    <span>{d.unitLabel}</span>
                    <span className="ml-auto">~{d.minutesPerUnit}′/{d.unitLabel.replace(/s$/, "")}</span>
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={confirmSave} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("breaks.items.save")}
            </Button>
          </DialogContent>
        </Dialog>
      )}

      {bookOpen && <BookDialog periodId={periodId} onClose={() => setBookOpen(false)} />}
    </div>
  );
}

function BookDialog({ periodId, onClose }: { periodId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const save = useServerFn(savePeriodItems);
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [pages, setPages] = useState(200);
  const [current, setCurrent] = useState(0);
  const [due, setDue] = useState("");
  const [finalTask, setFinalTask] = useState("none");
  const [busy, setBusy] = useState(false);

  const days = due ? Math.max(1, Math.round((new Date(`${due}T00:00:00Z`).getTime() - Date.now()) / 86400000)) : null;
  const perDay = days ? Math.ceil(Math.max(0, pages - current) / days) : null;

  async function submit() {
    setBusy(true);
    try {
      await save({
        data: {
          periodId,
          source: "manual",
          items: [{
            kind: "book", subject: "", title: title || "—", detail: author,
            unitLabel: "pages", totalUnits: pages, doneUnits: current, startUnit: 1,
            difficulty: "easy", minutesPerUnit: 2, dueDate: due || null,
            finalTask: finalTask === "none" ? null : finalTask,
          }],
        },
      });
      qc.invalidateQueries({ queryKey: ["study-period-items", periodId] });
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("breaks.errors.generic"));
    } finally { setBusy(false); }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl sm:max-w-md">
        <DialogHeader><DialogTitle className="text-base">{t("breaks.items.book")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">{t("breaks.items.bookTitle")}</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} /></div>
          <div className="space-y-1.5"><Label className="text-xs">{t("breaks.items.author")}</Label>
            <Input value={author} onChange={(e) => setAuthor(e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">{t("breaks.items.pages")}</Label>
              <Input type="number" value={pages} onChange={(e) => setPages(Number(e.target.value) || 0)} /></div>
            <div className="space-y-1.5"><Label className="text-xs">{t("breaks.items.currentPage")}</Label>
              <Input type="number" value={current} onChange={(e) => setCurrent(Number(e.target.value) || 0)} /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">{t("breaks.items.finishBy")}</Label>
            <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} /></div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("breaks.items.finalTask")}</Label>
            <div className="flex flex-wrap gap-1.5">
              {(["none", "summary", "sheet", "presentation", "report", "questions"] as const).map((f) => (
                <button
                  key={f} type="button" onClick={() => setFinalTask(f)}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                    finalTask === f ? "bg-primary text-primary-foreground" : "bg-muted text-foreground/70"
                  }`}
                >
                  {t(`breaks.items.finalTasks.${f}`)}
                </button>
              ))}
            </div>
          </div>
          {perDay !== null && (
            <p className="rounded-2xl bg-primary/10 p-3 text-xs">{t("breaks.items.perDay", { n: perDay, unit: t("breaks.items.pages").toLowerCase() })}</p>
          )}
          <Button onClick={submit} disabled={busy || !title.trim()}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("breaks.items.save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------- STUDY ----------------------------------- */

type Exercise = { prompt: string; expected: string; topic: string; level: string };
type Correction = {
  verdict: "correct" | "partly" | "wrong"; whereWrong: string; why: string;
  howToFix: string; rule: string; similarExercise: string; score: number;
};

function StudySection({ period }: { period: PeriodRow }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const genEx = useServerFn(generateExercises);
  const fix = useServerFn(correctExercise);
  const diag = useServerFn(generateDiagnostic);
  const sim = useServerFn(simulateExam);
  const bump = useServerFn(updateReadiness);

  const [topic, setTopic] = useState("");
  const [level, setLevel] = useState<"base" | "intermediate" | "advanced" | "exam">("base");
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [corrections, setCorrections] = useState<Record<number, Correction>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [quiz, setQuiz] = useState<{ question: string; options: string[]; correctIndex: number | null; topic: string; explanation: string }[] | null>(null);
  const [quizAnswers, setQuizAnswers] = useState<Record<number, number>>({});
  const [quizDone, setQuizDone] = useState(false);
  const [mock, setMock] = useState<{ title: string; instructions: string; tasks: { prompt: string; points: number; topic: string; solution: string }[] } | null>(null);
  const [showSolutions, setShowSolutions] = useState(false);
  const [mockMinutes, setMockMinutes] = useState(45);

  const errors = useQuery({
    queryKey: ["study-errors", period.subject_name],
    queryFn: async () => {
      const { data } = await supabase
        .from("study_error_log").select("*")
        .eq("subject", period.subject_name ?? "").order("occurrences", { ascending: false });
      return (data ?? []) as { id: string; topic: string; error_type: string; occurrences: number; rule: string | null }[];
    },
  });

  async function runDiagnostic() {
    setBusy("diag");
    try {
      const r = await diag({ data: { periodId: period.id, topics: topic ? [topic] : [], count: 10 } });
      setQuiz(r.questions.map((q) => ({ ...q, options: q.options, correctIndex: q.correctIndex })));
      setQuizAnswers({}); setQuizDone(false);
    } catch (e) { toast.error(e instanceof Error ? e.message : t("breaks.errors.generic")); }
    finally { setBusy(null); }
  }

  async function finishQuiz() {
    setQuizDone(true);
    if (!quiz) return;
    const correct = quiz.filter((q, i) => q.correctIndex !== null && quizAnswers[i] === q.correctIndex).length;
    const score = Math.round((correct / quiz.length) * 100);
    await bump({ data: { periodId: period.id, signal: score } });
    qc.invalidateQueries({ queryKey: ["study-period", period.id] });
    toast.success(`${correct}/${quiz.length}`);
  }

  async function makeExercises() {
    if (!topic.trim()) return;
    setBusy("ex");
    try {
      const r = await genEx({ data: { periodId: period.id, topic: topic.trim(), level, count: 3 } });
      setExercises(r.exercises); setAnswers({}); setCorrections({});
    } catch (e) { toast.error(e instanceof Error ? e.message : t("breaks.errors.generic")); }
    finally { setBusy(null); }
  }

  async function correctOne(i: number) {
    const ex = exercises[i];
    const ans = answers[i];
    if (!ans?.trim()) return;
    setBusy(`fix-${i}`);
    try {
      const r = await fix({ data: { periodId: period.id, exercise: ex.prompt, expected: ex.expected, answer: ans, topic: ex.topic } });
      setCorrections((p) => ({ ...p, [i]: r as Correction }));
      errors.refetch();
    } catch (e) { toast.error(e instanceof Error ? e.message : t("breaks.errors.generic")); }
    finally { setBusy(null); }
  }

  async function runMock() {
    setBusy("mock");
    try {
      const r = await sim({ data: { periodId: period.id, minutes: mockMinutes, topics: topic ? [topic] : [] } });
      setMock(r); setShowSolutions(false);
    } catch (e) { toast.error(e instanceof Error ? e.message : t("breaks.errors.generic")); }
    finally { setBusy(null); }
  }

  return (
    <div className="space-y-4">
      <Card className="card-soft space-y-2 rounded-3xl border-0 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <ClipboardCheck className="h-4 w-4 text-primary" /> {t("breaks.study.diagnostic")}
        </div>
        <p className="text-xs text-muted-foreground">{t("breaks.study.diagnosticHint")}</p>
        <Button size="sm" onClick={runDiagnostic} disabled={busy === "diag"}>
          {busy === "diag" ? <Loader2 className="h-4 w-4 animate-spin" /> : t("breaks.study.startDiagnostic")}
        </Button>
        {quiz?.map((q, i) => (
          <div key={i} className="space-y-1.5 rounded-2xl bg-muted/50 p-3">
            <div className="text-xs font-medium">{i + 1}. {q.question}</div>
            {q.options.map((o, oi) => {
              const picked = quizAnswers[i] === oi;
              const right = quizDone && q.correctIndex === oi;
              const wrong = quizDone && picked && q.correctIndex !== oi;
              return (
                <button
                  key={oi} onClick={() => !quizDone && setQuizAnswers((p) => ({ ...p, [i]: oi }))}
                  className={`block w-full rounded-xl px-3 py-1.5 text-left text-xs transition ${
                    right ? "bg-primary/20" : wrong ? "bg-destructive/20" : picked ? "bg-primary/10" : "bg-background"
                  }`}
                >
                  {o}
                </button>
              );
            })}
            {quizDone && q.explanation && <p className="text-xs text-muted-foreground">{q.explanation}</p>}
          </div>
        ))}
        {quiz && !quizDone && <Button size="sm" variant="outline" onClick={finishQuiz}>OK</Button>}
      </Card>

      <Card className="card-soft space-y-2 rounded-3xl border-0 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <ListChecks className="h-4 w-4 text-primary" /> {t("breaks.study.exercises")}
        </div>
        <p className="text-xs text-muted-foreground">{t("breaks.study.exercisesHint")}</p>
        <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder={t("breaks.study.topicPh")} />
        <div className="flex flex-wrap gap-1.5">
          {(["base", "intermediate", "advanced", "exam"] as const).map((l) => (
            <button
              key={l} onClick={() => setLevel(l)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                level === l ? "bg-primary text-primary-foreground" : "bg-muted text-foreground/70"
              }`}
            >
              {t(`breaks.study.level.${l}`)}
            </button>
          ))}
        </div>
        <Button size="sm" onClick={makeExercises} disabled={busy === "ex" || !topic.trim()}>
          {busy === "ex" ? <Loader2 className="h-4 w-4 animate-spin" /> : t("breaks.study.generate")}
        </Button>

        {exercises.map((ex, i) => {
          const c = corrections[i];
          return (
            <div key={i} className="space-y-2 rounded-2xl bg-muted/50 p-3">
              <AssistantMessage content={ex.prompt} className="text-xs font-medium" />
              <Textarea
                rows={2} value={answers[i] ?? ""} placeholder={t("breaks.study.yourAnswerPh")}
                onChange={(e) => setAnswers((p) => ({ ...p, [i]: e.target.value }))}
              />
              <MediaAttachButton onText={(txt) => setAnswers((p) => ({ ...p, [i]: `${p[i] ?? ""}\n${txt}`.trim() }))} />
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => correctOne(i)} disabled={busy === `fix-${i}`}>
                {busy === `fix-${i}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t("breaks.study.correct")}
              </Button>
              {c && (
                <div className="space-y-1.5 rounded-xl bg-background p-3 text-xs leading-relaxed">
                  <Badge variant="secondary" className="rounded-full text-xs">{t(`breaks.study.verdict.${c.verdict}`)}</Badge>
                  {c.whereWrong && <p><strong>{t("breaks.study.whereWrong")}:</strong> <RichText content={c.whereWrong} /></p>}
                  {c.why && <p><strong>{t("breaks.study.why")}:</strong> <RichText content={c.why} /></p>}
                  {c.howToFix && <p><strong>{t("breaks.study.howToFix")}:</strong> <RichText content={c.howToFix} /></p>}
                  {c.rule && <p className="rounded-lg bg-primary/10 p-2"><strong>{t("breaks.study.rule")}:</strong> <RichText content={c.rule} /></p>}
                  {c.similarExercise && (
                    <div className="pt-1">
                      <div className="font-semibold">{t("breaks.study.similar")}</div>
                      <AssistantMessage content={c.similarExercise} />
                      <Button
                        size="sm" variant="ghost" className="mt-1 h-7 text-xs"
                        onClick={() => setExercises((p) => [...p, { prompt: c.similarExercise, expected: "", topic: ex.topic, level: ex.level }])}
                      >
                        <Plus className="mr-1 h-3 w-3" /> +
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </Card>

      <Card className="card-soft space-y-2 rounded-3xl border-0 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <AlertTriangle className="h-4 w-4 text-primary" /> {t("breaks.study.errorLog")}
        </div>
        {!errors.data?.length && <p className="text-xs text-muted-foreground">{t("breaks.study.errorLogEmpty")}</p>}
        {errors.data?.map((e) => (
          <div key={e.id} className="flex items-center gap-2 rounded-xl bg-muted/50 px-3 py-2 text-xs">
            <span className="min-w-0 flex-1 truncate">{e.topic ? `${e.topic} — ` : ""}{e.error_type}</span>
            <Badge variant="secondary" className="rounded-full text-xs">{t("breaks.study.times", { n: e.occurrences })}</Badge>
          </div>
        ))}
      </Card>

      <Card className="card-soft space-y-2 rounded-3xl border-0 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <ClipboardCheck className="h-4 w-4 text-primary" /> {t("breaks.study.simulate")}
        </div>
        <p className="text-xs text-muted-foreground">{t("breaks.study.simulateHint")}</p>
        <div className="flex flex-wrap gap-1.5">
          {[20, 45, 60].map((m) => (
            <button
              key={m} onClick={() => setMockMinutes(m)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                mockMinutes === m ? "bg-primary text-primary-foreground" : "bg-muted text-foreground/70"
              }`}
            >
              {m} min
            </button>
          ))}
        </div>
        <Button size="sm" onClick={runMock} disabled={busy === "mock"}>
          {busy === "mock" ? <Loader2 className="h-4 w-4 animate-spin" /> : t("breaks.study.startSim")}
        </Button>
        {mock && (
          <div className="space-y-2 rounded-2xl bg-muted/50 p-3 text-xs leading-relaxed">
            <div className="text-sm font-semibold">{mock.title}</div>
            {mock.instructions && <p className="text-muted-foreground">{mock.instructions}</p>}
            {mock.tasks.map((tk, i) => (
              <div key={i} className="space-y-1 rounded-xl bg-background p-3">
                <div className="flex gap-2">
                  <span className="font-medium">{i + 1}.</span>
                  <AssistantMessage content={tk.prompt} className="min-w-0 flex-1 [&_p]:mb-0" />
                  <span className="ml-auto shrink-0 text-muted-foreground">{t("breaks.study.points", { n: tk.points })}</span>
                </div>
                {showSolutions && tk.solution && (
                  <div className="rounded-lg bg-primary/10 p-2">
                    <strong>{t("breaks.study.solution")}:</strong>
                    <AssistantMessage content={tk.solution} />
                  </div>
                )}
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setShowSolutions(true)} disabled={showSolutions}>
              {t("breaks.study.showSolutions")}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
